/**
 * @file The collapsed "Show what I queried" panel under one agent answer. Lists, for each
 * `runNamedAnalyticsQuery` call behind the answer, the query, its parameters, the period, the
 * granted scope level, the snapshot and the builds it read. Rendered by `TranscriptMessage` in
 * `MessagesList` from the snapshot-derived `ChatMessage.analyticsQueries`. Starts collapsed and
 * passes `destroyOnHidden`, so the details mount only while the panel is open. The `aria-label`
 * carries the message id, so every answer's panel has its own name.
 */
import React from 'react';
import { Collapse, Typography, theme } from 'antd';

import { AgentMarkdownFontScale, type AnalyticsQueryProvenance, type AnalyticsScopeLevel } from '#types';

import FlexCol from '../atoms/FlexCol';

const { useToken } = theme;
const { Text } = Typography;

const SCOPE_LEVEL_LABEL: Record<AnalyticsScopeLevel, string> = {
  self: 'My sales',
  team: 'My team',
  org: 'Org',
};

const AnalyticsQueryDetails: React.FC<{ analyticsQueries: readonly AnalyticsQueryProvenance[] }> = ({ analyticsQueries }) => {
  const { token } = useToken();
  const secondaryFontSize = token.fontSize * AgentMarkdownFontScale.small;

  return (
    <FlexCol gap={10}>
      {analyticsQueries.map((provenance, index) => (
        <FlexCol key={`${provenance.query}-${provenance.snapshotId}-${index}`} gap={5}>
          <Text strong>{provenance.query}</Text>
          {Object.entries(provenance.parameters).map(([name, value]) => (
            <Text key={name}>{`${name}: ${value}`}</Text>
          ))}
          <Text>{`Period: ${provenance.period.label}`}</Text>
          <Text>{`Scope: ${SCOPE_LEVEL_LABEL[provenance.level]}`}</Text>
          <Text type="secondary" style={{ fontSize: secondaryFontSize }}>{`Snapshot ${provenance.snapshotId}`}</Text>
          {provenance.builds.map((build) => (
            <Text key={`${build.factTable}-${build.buildId}`} type="secondary" style={{ fontSize: secondaryFontSize }}>
              {`${build.factTable} build ${build.buildId}, generation ${build.generation}, cut ${build.cutTime}, built ${build.builtAt}`}
            </Text>
          ))}
        </FlexCol>
      ))}
    </FlexCol>
  );
};

type AnalyticsQueriesPanelProps = { messageId: string; analyticsQueries: readonly AnalyticsQueryProvenance[] };

const AnalyticsQueriesPanel: React.FC<AnalyticsQueriesPanelProps> = ({ messageId, analyticsQueries }) => (
  <Collapse
    ghost
    size="small"
    destroyOnHidden
    aria-label={`Show what I queried for answer ${messageId}`}
    items={[
      {
        key: 'analyticsQueries',
        label: <Text>Show what I queried</Text>,
        children: <AnalyticsQueryDetails analyticsQueries={analyticsQueries} />,
      },
    ]}
  />
);

export default AnalyticsQueriesPanel;
